import { Boxes, Headset, Loader2, MessagesSquare, Send, UserRound } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { fmtDateTime } from '../../shared/format'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useAddRequestMessage, useRequestMessages, useRequests, useSaveRequest } from '../api'
import { REQUEST_STATUSES, type RequestStatus, type SupportRequest } from '../types'
import { toastError, useCrm } from '../ui'
import { PriorityBadge } from './CrmBits'

/** Destek talebinin yazışma geçmişi; yanıt yazılır, durum buradan değiştirilir. */
export function RequestThread({ requestId, canWrite, onClose }: { requestId: string; canWrite: boolean; onClose: () => void }) {
  const { t } = useTranslation()
  const { staffName, customerName } = useCrm()
  const { user } = useAuth()
  const r = useRequests().data?.find((x) => x.id === requestId)
  const thread = useRequestMessages(requestId)
  const messages = thread.data ?? []
  const send = useAddRequestMessage()
  const save = useSaveRequest()
  const [body, setBody] = useState('')
  const endRef = useRef<HTMLDivElement>(null)
  const count = messages.length

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' })
  }, [count])

  if (!r) return null

  const submit = () => {
    if (body.trim().length < 2) return
    send.mutate({ requestId: r.id, body }, { onSuccess: () => setBody(''), onError: toastError })
  }
  const setStatus = (status: RequestStatus) => {
    const { id, number: _n, createdAt: _c, messages: _m, ...input } = r as SupportRequest
    save.mutate({ id, input: { ...input, status } }, { onError: toastError })
  }

  return (
    <Modal open size="lg" onOpenChange={(v) => !v && onClose()} icon={<Headset />} title={`${r.number} · ${r.title}`} description={customerName(r.customerId)} bodyClassName="p-0">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-5 py-3 text-xs text-muted-foreground">
        <PriorityBadge priority={r.priority} />
        <span className="inline-flex items-center gap-1">
          <Boxes className="size-3.5" aria-hidden />
          {r.module || t('crm.request.general')}
        </span>
        <span className="inline-flex items-center gap-1">
          <UserRound className="size-3.5" aria-hidden />
          {r.assigneeId ? staffName(r.assigneeId) : t('crm.unassigned')}
        </span>
        <span className="tabular-nums">{fmtDateTime(r.createdAt)}</span>
        <Select size="sm" className="ml-auto w-40" value={r.status} onChange={(v) => setStatus(v as RequestStatus)} disabled={!canWrite || save.isPending} aria-label={t('crm.fields.status')} options={REQUEST_STATUSES.map((s) => ({ value: s, label: t(`crm.requestStatus.${s}`) }))} />
      </div>

      <div className="max-h-[50vh] space-y-3 overflow-y-auto scrollbar-none bg-muted/30 px-5 py-4">
        {r.description ? (
          <div className="rounded-xl border border-dashed bg-card p-3 text-sm">
            <p className="mb-1 text-xs font-medium text-muted-foreground">{t('crm.fields.description')}</p>
            <p className="whitespace-pre-wrap">{r.description}</p>
          </div>
        ) : null}
        {thread.isLoading ? (
          <p className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden />
          </p>
        ) : messages.length === 0 ? (
          <p className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
            <MessagesSquare className="size-4" aria-hidden />
            {t('crm.request.noMessages')}
          </p>
        ) : (
          messages.map((m) => {
            const mine = m.author === user?.id
            const author = staffName(m.author)
            return (
              <div key={m.id} className={cn('flex items-end gap-2', mine && 'flex-row-reverse')}>
                <Avatar name={author} className="size-7 shrink-0" />
                <div className={cn('max-w-[80%] rounded-2xl px-3.5 py-2 text-sm shadow-xs', mine ? 'rounded-br-md bg-primary text-primary-foreground' : 'rounded-bl-md bg-card')}>
                  <p className={cn('mb-0.5 text-xs font-medium', mine ? 'text-primary-foreground/80' : 'text-muted-foreground')}>
                    {author} · <span className="tabular-nums">{fmtDateTime(m.at)}</span>
                  </p>
                  <p className="whitespace-pre-wrap break-words">{m.body}</p>
                </div>
              </div>
            )
          })
        )}
        <div ref={endRef} />
      </div>

      {canWrite ? (
        <form
          className="flex items-end gap-2 border-t p-4"
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
        >
          <Textarea
            rows={2}
            value={body}
            maxLength={2000}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit()
            }}
            placeholder={t('crm.request.replyPlaceholder')}
            aria-label={t('crm.request.replyPlaceholder')}
          />
          <Button type="submit" disabled={body.trim().length < 2 || send.isPending} aria-label={t('crm.request.send')}>
            {send.isPending ? <Loader2 className="animate-spin" /> : <Send />}
            <span className="hidden sm:inline">{t('crm.request.send')}</span>
          </Button>
        </form>
      ) : null}
    </Modal>
  )
}
